// Keycloak OAuth2 service for reservation portal
// Mirrors ParkezeOperator's keycloakService.ts but uses 'parkeze-reserve' client
import { getEmbedRuntime } from '@/embed/runtime'

let _resolveAuthReady: () => void
let _authReadyResolved = false
export const authReady = new Promise<void>((resolve) => {
  _resolveAuthReady = resolve
})

export function signalAuthReady(): void {
  if (!_authReadyResolved) {
    _authReadyResolved = true
    _resolveAuthReady()
  }
}

setTimeout(() => signalAuthReady(), 30_000)

function isValidRealm(realm: string): boolean {
  return typeof realm === 'string' && /^[a-zA-Z0-9-]+$/.test(realm) && realm.length <= 64
}

function getClientId(): string {
  return import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'parkeze-reserve'
}

function getKeycloakBaseUrl(): string {
  return import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.parkeze.com'
}

function buildTokenEndpoint(realm: string): string {
  if (!isValidRealm(realm)) throw new Error('Invalid realm name')
  return `${getKeycloakBaseUrl()}/realms/${realm}/protocol/openid-connect/token`
}
export async function exchangeCodeForToken(code: string, realm: string, codeVerifier?: string): Promise<void> {
  const tokenEndpoint = buildTokenEndpoint(realm)
  const redirectUri = window.location.origin + '/'

  const body: Record<string, string> = {
    grant_type: 'authorization_code',
    client_id: getClientId(),
    code,
    redirect_uri: redirectUri,
  }
  if (codeVerifier) body.code_verifier = codeVerifier

  const resp = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body),
  })

  if (!resp.ok) throw new Error(`Token exchange failed: ${resp.status}`)

  const data = await resp.json()
  if (!data.access_token) throw new Error('No access token in response')

  const expiresIn = data.expires_in || 300
  storeToken(data.access_token, data.refresh_token, expiresIn, data.token_type)
  scheduleTokenRefresh(expiresIn)
}

function storeToken(
  accessToken: string,
  refreshTok: string | null,
  expiresIn: number,
  tokenType?: string,
): void {
  localStorage.setItem(
    'keycloak-token',
    JSON.stringify({
      access_token: accessToken,
      refresh_token: refreshTok || null,
      expires_at: new Date(Date.now() + expiresIn * 1000).toISOString(),
      token_type: tokenType || 'Bearer',
    }),
  )
  tokenListeners.forEach((listener) => listener())
}

const tokenListeners = new Set<() => void>()

/**
 * Called whenever the access token changes: a refresh here, or one another tab
 * wrote. The modules the shell mounts hold the token as an attribute (decision
 * 14's contract), so the shell hands each new one in before the old one dies,
 * rather than waiting for a 401. Returns the unsubscribe.
 */
export function onTokenChange(listener: () => void): () => void {
  const onStorage = (e: StorageEvent) => { if (e.key === 'keycloak-token') listener() }
  tokenListeners.add(listener)
  window.addEventListener('storage', onStorage)
  return () => {
    tokenListeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

/** Back to the landing page to sign in, returning here afterwards. */
export function redirectToSignIn(): void {
  // The landing page is already signing in.
  if (window.location.pathname === '/') return
  const intended = window.location.pathname + window.location.search
  if (intended !== '/') localStorage.setItem('post-login-redirect', intended)
  window.location.href = '/'
}

export function getToken(): string | undefined {
  const str = localStorage.getItem('keycloak-token')
  if (!str) return undefined
  try {
    return JSON.parse(str).access_token
  } catch {
    return undefined
  }
}

/**
 * The signed-in user's token claims. Embedded (logbook decision 12), the token
 * is the one the host handed in -- the host's storage is not ours to read -- so
 * every role check below answers for the person the host signed in.
 */
export function getUserInfo(): Record<string, unknown> | null {
  const embed = getEmbedRuntime()
  const str = embed ? null : localStorage.getItem('keycloak-token')
  if (!embed && !str) return null
  try {
    const access_token = embed ? embed.getToken() : JSON.parse(str!).access_token
    if (!access_token) return null
    const payload = access_token.split('.')[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(
      decodeURIComponent(
        decoded
          .split('')
          .map((c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      ),
    )
  } catch {
    return null
  }
}
/**
 * The signed-in person's Keycloak account page (their name, email, password,
 * sessions), in the realm they signed in to. The referrer parameters give
 * that page a link back here.
 */
export function accountConsoleUrl(): string {
  let realm = 'parkeze'
  try {
    const info = JSON.parse(localStorage.getItem('realm-info') || '{}')
    if (isValidRealm(info.realm)) realm = info.realm
  } catch { /* the default realm */ }
  const back = encodeURIComponent(window.location.origin + '/home')
  return `${getKeycloakBaseUrl()}/realms/${realm}/account?referrer=${getClientId()}&referrer_uri=${back}`
}

export function logout(): void {
  const realmInfoStr = localStorage.getItem('realm-info')
  localStorage.clear()
  sessionStorage.clear()

  if (realmInfoStr) {
    try {
      const { realm } = JSON.parse(realmInfoStr)
      if (isValidRealm(realm)) {
        const base = getKeycloakBaseUrl()
        const redirect = encodeURIComponent(window.location.origin + '/')
        window.location.href =
          `${base}/realms/${realm}/protocol/openid-connect/logout` +
          `?client_id=${getClientId()}&post_logout_redirect_uri=${redirect}`
        return
      }
    } catch { /* fall through */ }
  }
  window.location.href = '/'
}

let refreshPromise: Promise<boolean> | null = null
let refreshTimerId: ReturnType<typeof setTimeout> | null = null

async function performTokenRefresh(): Promise<boolean> {
  const tokenStr = localStorage.getItem('keycloak-token')
  const realmStr = localStorage.getItem('realm-info')
  // Another tab signed out: so has this one.
  if (!tokenStr) {
    redirectToSignIn()
    return false
  }
  if (!realmStr) return false

  try {
    const td = JSON.parse(tokenStr)
    const ri = JSON.parse(realmStr)
    if (!td.refresh_token) return false

    const endpoint = buildTokenEndpoint(ri.realm)
    const resp = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: getClientId(),
        refresh_token: td.refresh_token,
      }),
    })

    if (!resp.ok) {
      // Keycloak refused the refresh token: the session ended (signed out in
      // another app or tab, or timed out). Sign in again.
      if (resp.status === 400 || resp.status === 401) {
        localStorage.removeItem('keycloak-token')
        redirectToSignIn()
      }
      return false
    }

    const data = await resp.json()
    if (!data.access_token) return false

    const expiresIn = data.expires_in || 300
    storeToken(data.access_token, data.refresh_token || td.refresh_token, expiresIn, data.token_type)
    scheduleTokenRefresh(expiresIn)
    return true
  } catch {
    return false
  }
}

/**
 * Refresh tokens are single use in the parkeze realm, and every tab of the
 * portal shares one in localStorage. Two tabs spending it at once got one of
 * them a 400 and a sign-out. So tabs take turns, and a tab that waited behind
 * another adopts the token that one stored instead of spending a spent one.
 */
const REFRESH_LOCK = 'parkeze-reserve-token-refresh'

function withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
  return navigator.locks ? (navigator.locks.request(REFRESH_LOCK, work) as Promise<T>) : work()
}

function readStoredToken(): { refresh_token?: string; access_token?: string } | null {
  try {
    return JSON.parse(localStorage.getItem('keycloak-token') || 'null')
  } catch {
    return null
  }
}

export async function refreshToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  const spending = readStoredToken()?.refresh_token
  refreshPromise = withRefreshLock(async () => {
    const stored = readStoredToken()
    if (stored?.refresh_token && stored.refresh_token !== spending && isAuthenticated()) {
      scheduleTokenRefresh()
      return true
    }
    return performTokenRefresh()
  }).finally(() => { refreshPromise = null })
  return refreshPromise
}

let watchingSession = false

/**
 * Keep this tab in step with the browser's one Keycloak session. Coming back
 * to the tab asks Keycloak (a refresh) whether the session still stands, so a
 * sign-out in another Parkeze app shows here at once rather than at the next
 * scheduled refresh; a token another tab refreshed moves this tab's timer.
 */
export function watchSession(): void {
  if (watchingSession) return
  watchingSession = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    const iat = getUserInfo()?.iat
    if (typeof iat !== 'number' || Date.now() / 1000 - iat > 60) void refreshToken()
  })
  window.addEventListener('storage', (e) => {
    if (e.key === 'keycloak-token' && e.newValue) scheduleTokenRefresh()
  })
}

export function scheduleTokenRefresh(expiresInSeconds?: number): void {
  if (refreshTimerId) { clearTimeout(refreshTimerId); refreshTimerId = null }

  let delayMs: number

  if (expiresInSeconds !== undefined) {
    delayMs = Math.max(expiresInSeconds * 0.75 * 1000, (expiresInSeconds - 30) * 1000)
    delayMs = Math.max(delayMs, 10_000)
  } else {
    const str = localStorage.getItem('keycloak-token')
    if (!str) return
    try {
      const remaining = new Date(JSON.parse(str).expires_at).getTime() - Date.now()
      if (remaining <= 0) { refreshToken(); return }
      delayMs = Math.max(remaining * 0.75, Math.min(remaining - 30_000, 10_000))
      delayMs = Math.max(delayMs, 10_000)
    } catch { return }
  }

  refreshTimerId = setTimeout(() => refreshToken(), delayMs)
}

export function isAuthenticated(): boolean {
  const str = localStorage.getItem('keycloak-token')
  if (!str) return false
  try {
    const td = JSON.parse(str)
    if (!td.access_token) return false
    const exp = td.expires_at ? new Date(td.expires_at) : null
    return !exp || exp > new Date()
  } catch {
    return false
  }
}

// Decision 34: Keycloak resolves what someone may do per organization into
// `parkeze_access: {alias: {actions, station_actions, admin, …}}`.
interface AccessEntry { actions?: string[]; station_actions?: string[]; admin?: boolean }

function accessClaim(): Record<string, AccessEntry> {
  const claim = getUserInfo()?.parkeze_access
  return claim && typeof claim === 'object' ? (claim as Record<string, AccessEntry>) : {}
}

/** Whether any organization in the token lets them do `action`. */
function mayDo(action: string): boolean {
  return Object.values(accessClaim()).some((e) => (e?.actions ?? []).includes(action))
}

// Returns true if the token lets them do anything with reservations.
// Users with no access (e.g. "student") must not be allowed past AuthGuard.
export function hasAnyReservationRole(): boolean {
  return Object.values(accessClaim()).some((e) =>
    [...(e?.actions ?? []), ...(e?.station_actions ?? [])].some((a) => a.startsWith('reservation.') || a === 'enforcement.check'))
}

export function isReservationAdmin(): boolean {
  return mayDo('reservation.admin')
}

/** An administrator of the organization (decisions 24, 34); the API decides either way. */
export function isOrgAdministrator(): boolean {
  return Object.values(accessClaim()).some((e) => e?.admin === true)
}

export function isReservationViewer(): boolean {
  return mayDo('reservation.view_all')
}

/**
 * An enforcement officer, as reservation-api and live-api decide it:
 * `enforcement.check` (decision 34). So the kiosk is offered to exactly the
 * people whose checks and violation stream will work -- nobody gets a button
 * that leads to 403s.
 */
export function isEnforcementOfficer(): boolean {
  return mayDo('enforcement.check')
}
